/**
 * WebAudio の共通基盤。
 *
 * ・AudioContext を1つだけ持ち、BGM / 効果音のバスを分けて音量管理する
 * ・ブラウザの自動再生制限に備え、最初のユーザー操作で resume() を呼べるようにする
 * ・WebAudio が使えない環境では null を返し、呼び出し側は静かに無視する
 *
 * 音源ファイルを一切使わず、すべて合成で鳴らすためオリジナル素材のみで完結する。
 */

export interface AudioBus {
  ctx: AudioContext;
  /** 効果音の接続先。 */
  out: GainNode;
  /** ホワイトノイズ（打撃音・シェイカー用）。 */
  noise: AudioBuffer;
}

/** 減衰の共通形（アタック → 指数減衰）。 */
export function envelope(
  gain: GainNode,
  now: number,
  peak: number,
  attack: number,
  decay: number,
): void {
  const safePeak = Math.max(0.0001, peak);
  gain.gain.cancelScheduledValues(now);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(safePeak, now + Math.max(0.001, attack));
  gain.gain.exponentialRampToValueAtTime(0.0001, now + Math.max(0.02, attack + decay));
}

/** 録画の音の持ち上げ（約+10dB）。スピーカー側には掛けない。 */
export const RECORDING_GAIN = 3.16;

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private seBus: GainNode | null = null;
  private bgmBus: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private master: GainNode | null = null;
  /** 名場面の録画用に分けた出口（録っているあいだだけつなぐ）。 */
  private recordTap: MediaStreamAudioDestinationNode | null = null;
  /** 録画用の分岐の頭（master → 持ち上げ → リミッター → recordTap）。 */
  private recordBoost: GainNode | null = null;
  private unavailable = false;

  private seVolume = 1;
  private bgmVolume = 1;

  /** AudioContext を用意する（未対応環境では null）。 */
  ensure(): AudioContext | null {
    if (this.ctx) return this.ctx;
    if (this.unavailable) return null;
    try {
      const Ctor =
        typeof window === 'undefined'
          ? undefined
          : (window.AudioContext ??
            (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
      if (!Ctor) {
        this.unavailable = true;
        return null;
      }
      const ctx = new Ctor();
      const master = ctx.createGain();
      master.gain.value = 0.9;
      master.connect(ctx.destination);

      const seBus = ctx.createGain();
      seBus.gain.value = this.seVolume;
      seBus.connect(master);

      const bgmBus = ctx.createGain();
      bgmBus.gain.value = this.bgmVolume;
      bgmBus.connect(master);

      this.ctx = ctx;
      this.master = master;
      this.seBus = seBus;
      this.bgmBus = bgmBus;
      return ctx;
    } catch {
      this.unavailable = true;
      return null;
    }
  }

  /** 自動再生制限の解除（ユーザー操作の直後に呼ぶ）。 */
  resume(): void {
    const ctx = this.ensure();
    // iOS では広告や電話のあと 'interrupted' になる（'suspended' だけを見ていると戻せない）
    if (!ctx || ctx.state === 'running' || ctx.state === 'closed') return;
    void ctx.resume().catch(() => undefined);
  }

  get available(): boolean {
    return this.ensure() !== null;
  }

  /** 効果音用のバス。 */
  seBusOrNull(): AudioBus | null {
    const ctx = this.ensure();
    if (!ctx || !this.seBus) return null;
    return { ctx, out: this.seBus, noise: this.noise(ctx) };
  }

  /** BGM 用のバス。 */
  bgmBusOrNull(): AudioBus | null {
    const ctx = this.ensure();
    if (!ctx || !this.bgmBus) return null;
    return { ctx, out: this.bgmBus, noise: this.noise(ctx) };
  }

  /**
   * 名場面の録画用に、鳴っている音（効果音・合成BGM）を MediaStream として分けて取り出す。
   * スピーカーへの経路はそのまま（分岐を1本足すだけ）。使えない環境では null。
   */
  openRecordingTap(): MediaStream | null {
    if (this.recordTap) return this.recordTap.stream;
    const ctx = this.ensure();
    if (!ctx || !this.master || typeof ctx.createMediaStreamDestination !== 'function') return null;
    try {
      const tap = ctx.createMediaStreamDestination();
      // 録画の音は小さく入る（BGM30%で平均−43dB）ので、録画側だけ約+10dB 持ち上げる。
      // 音割れしないよう、すぐあとに簡単なリミッター（圧縮率の高いコンプレッサー）を挟む。
      // スピーカー側（master → destination）の音量は変わらない。
      const boost = ctx.createGain();
      boost.gain.value = RECORDING_GAIN;
      let head: AudioNode = boost;
      if (typeof ctx.createDynamicsCompressor === 'function') {
        const limiter = ctx.createDynamicsCompressor();
        limiter.threshold.value = -6;
        limiter.knee.value = 0;
        limiter.ratio.value = 20;
        limiter.attack.value = 0.003;
        limiter.release.value = 0.25;
        boost.connect(limiter);
        head = limiter;
      }
      head.connect(tap);
      this.master.connect(boost);
      this.recordBoost = boost;
      this.recordTap = tap;
      return tap.stream;
    } catch {
      return null;
    }
  }

  /** 録画用の分岐を外す。 */
  closeRecordingTap(): void {
    const tap = this.recordTap;
    const boost = this.recordBoost;
    this.recordTap = null;
    this.recordBoost = null;
    if (!tap) return;
    try {
      if (boost) this.master?.disconnect(boost);
      else this.master?.disconnect(tap);
    } catch {
      /* noop */
    }
  }

  setSeVolume(volume: number): void {
    this.seVolume = clamp01(volume);
    if (this.seBus && this.ctx) {
      this.seBus.gain.setTargetAtTime(this.seVolume, this.ctx.currentTime, 0.02);
    }
  }

  setBgmVolume(volume: number): void {
    this.bgmVolume = clamp01(volume);
    if (this.bgmBus && this.ctx) {
      this.bgmBus.gain.setTargetAtTime(this.bgmVolume, this.ctx.currentTime, 0.05);
    }
  }

  close(): void {
    const ctx = this.ctx;
    this.ctx = null;
    this.master = null;
    this.recordTap = null;
    this.recordBoost = null;
    this.seBus = null;
    this.bgmBus = null;
    this.noiseBuffer = null;
    try {
      void ctx?.close();
    } catch {
      /* noop */
    }
  }

  /** 打撃音・シェイカー用のノイズ（1秒分を使い回す）。 */
  private noise(ctx: AudioContext): AudioBuffer {
    if (this.noiseBuffer) return this.noiseBuffer;
    const length = Math.floor(ctx.sampleRate);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let seed = 1337;
    for (let i = 0; i < length; i += 1) {
      // 決定的な擬似乱数（毎回同じノイズにして音色を安定させる）
      seed = (seed * 1103515245 + 12345) % 2147483647;
      data[i] = (seed / 2147483647) * 2 - 1;
    }
    this.noiseBuffer = buffer;
    return buffer;
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}
